import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  Alert,
  Tooltip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  LockOpenOutlined as UnlockIcon,
  SecurityOutlined as SecurityIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import EncryptionBadge from './EncryptionBadge';

interface EncryptionSettingsProps {
  isEncrypted: boolean;
  onToggleEncryption: (encrypt: boolean) => void;
  disabled?: boolean;
}

const EncryptionSettings: React.FC<EncryptionSettingsProps> = ({
  isEncrypted,
  onToggleEncryption,
  disabled = false
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleToggleEncryption = (event: React.ChangeEvent<HTMLInputElement>) => {
    onToggleEncryption(event.target.checked);
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SecurityIcon color="primary" sx={{ mr: 1.5 }} />
          <Typography variant="h6">Document Encryption</Typography>
        </Box>
        <EncryptionBadge isEncrypted={isEncrypted} size="medium" />
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={isEncrypted}
              onChange={handleToggleEncryption}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {isEncrypted ? 'Encryption Enabled' : 'Encryption Disabled'}
            </Typography>
          }
        />
      </Box>

      {isEncrypted ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            This document is encrypted with AES-256 encryption. Only you and users you've shared it with can access the content.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            This document is not encrypted. We recommend enabling encryption for sensitive legal documents.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={toggleDetails}>
        <Typography variant="body2" color="primary">
          {showDetails ? 'Hide encryption details' : 'Show encryption details'}
        </Typography>
        {showDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={showDetails}>
        <Box sx={{ mt: 2, bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Encryption Details:
          </Typography>
          <List dense disablePadding>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Algorithm" 
                secondary="AES-256-CBC (Advanced Encryption Standard)" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Key Derivation" 
                secondary="User and document-specific keys" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Storage" 
                secondary="Encrypted at rest in the database" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Transmission" 
                secondary="Encrypted in transit via HTTPS" 
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
            Note: Encryption adds an extra layer of security but may slightly impact performance when loading or saving large documents.
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EncryptionSettings; 